import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('spamhaus-listing');

/**
 * The diagnostic page of the deliverability cluster. Its asset is the return-code table: the last
 * octet of the DNSBL response names the sub-list, and the sub-list determines the entire remedy.
 * Most delisting attempts fail because people never look at it.
 */
export default function SpamhausListingPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Spamhaus is not one list. ZEN combines SBL, CSS, XBL and PBL, and DBL lists domains separately. Each has a different cause and a different remedy, and the DNS response tells you which one you are on. Removal requests fail mostly because people request removal from the wrong list.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="First, find out which list you are on">
        <p>
          The lookup returns an address in <Tok>127.0.0.x</Tok>, and the last octet is the answer.
          Every remedy below depends on it, so do this before anything else:
        </p>
        <LearnCode>
          {`# reverse the octets of your IP, then query the zen zone
dig +short 7.113.0.203.zen.spamhaus.org
→ 127.0.0.4`}
        </LearnCode>
        <LearnTable
          head={['Response', 'List', 'Means']}
          rows={[
            ['127.0.0.2', 'SBL', 'Spamhaus determined this IP sent spam'],
            ['127.0.0.3', 'CSS', 'Automated: snowshoe or low-reputation sending pattern'],
            ['127.0.0.4 – .7', 'XBL', 'The host looks compromised — exploit, proxy or bot'],
            ['127.0.0.9', 'SBL (DROP)', 'The whole allocation is considered hijacked'],
            ['127.0.0.10 / .11', 'PBL', <Lead key="a">Not a spam listing — see below</Lead>],
          ]}
        />
        <p>
          Skipping this step is why so many delisting attempts fail. A PBL listing and an SBL
          listing look identical in a checker that reports only "listed on Spamhaus", and they have
          nothing in common beyond the name.
        </p>
      </LearnSection>

      <LearnSection title="PBL is not an accusation">
        <p>
          The most misread listing of the five. PBL is a directory of IP ranges that the <em>network
          operator</em> has declared should not be sending mail directly — residential broadband,
          dynamic ranges, cloud instances not designated as mail servers.
        </p>
        <p>
          Being on it means nobody registered your IP as a mail server. It does not mean you sent
          spam, and no reputation damage attaches to it. Two remedies:
        </p>
        <LearnList>
          <li>
            <Lead>Do not send directly.</Lead> Relay through your provider’s SMTP or an ESP. This is
            the right answer for almost everyone, because a residential or default cloud IP has no
            sending history and will struggle regardless.
          </li>
          <li>
            <Lead>Ask the network operator to reclassify the range</Lead> if you genuinely run a
            mail server there. Self-removal exists for some ranges; where it does not, only the
            operator can change it.
          </li>
        </LearnList>
      </LearnSection>

      <LearnSection title="XBL means investigate before you request anything">
        <p>
          XBL says the host looks compromised — an open proxy, an exploited service, or malware
          sending on its own. Requesting removal without finding the cause gets you relisted almost
          immediately, because the thing sending is still sending.
        </p>
        <p>What to check, in order:</p>
        <LearnList ordered>
          <li>Mail queue depth. Thousands of unexplained messages is the signature of a compromised account.</li>
          <li>Whether your SMTP service is accepting relay from outside your network.</li>
          <li>Application accounts with weak or reused credentials — a compromised contact form or CMS is a common vector.</li>
          <li>Outbound connections on port 25 from processes that have no business making them.</li>
        </LearnList>
        <p>
          XBL removals are self-service and take effect quickly. They are also automatic once the
          detection stops, so if you have genuinely fixed it, waiting works too.
        </p>
      </LearnSection>

      <LearnSection title="SBL and CSS are about your sending">
        <LearnSubsection title="SBL">
          <p>
            Evidence-based and usually manual: spam was received from this IP, or the IP belongs to
            a range Spamhaus has determined is under spammer control. The removal form asks what
            changed. A request that does not describe a concrete fix generally does not succeed.
          </p>
        </LearnSubsection>
        <LearnSubsection title="CSS">
          <p>
            Automated, and the one legitimate senders hit most. It targets patterns rather than
            reported spam: new IPs sending volume with no history, ranges rotating across many IPs
            to spread volume thinly — snowshoeing — and sending to addresses that have never
            engaged.
          </p>
          <p>
            A CSS listing is frequently a warmup problem rather than a spam problem: too much volume
            too early from an IP nobody has seen before. Fixing it means{' '}
            <Link to="/learn/email-warmup" className="text-accent underline underline-offset-4">
              ramping properly
            </Link>{' '}
            and cleaning the list, then requesting removal. CSS also expires on its own once the
            pattern stops.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="DBL lists your domain, and that is worse">
        <p>
          Everything above concerns IPs. DBL lists <em>domains</em>, and it is the more damaging
          listing precisely because there is nowhere to move — changing ESP or IP takes the listing
          with you.
        </p>
        <p>Three ways a domain gets listed, and the third surprises people:</p>
        <LearnList>
          <li>It sent spam, or was the visible sender of it.</li>
          <li>It appeared as a <em>link</em> inside spam sent by somebody else — an affiliate, a partner, or an attacker.</li>
          <li>A page on your site was compromised and is serving something malicious.</li>
        </LearnList>
        <p>
          The second and third mean a domain can be listed without you sending a single message.
          Check whether your site is clean before assuming your mail is the cause, and see{' '}
          <Link to="/learn/email-blacklist" className="text-accent underline underline-offset-4">
            how domain listings differ from IP listings
          </Link>
          .
        </p>
      </LearnSection>

      <LearnSection title="Requesting removal">
        <LearnList ordered>
          <li>
            <Lead>Confirm the sub-list</Lead> from the return code. Everything else depends on it.
          </li>
          <li>
            <Lead>Fix the cause.</Lead> Non-negotiable. Relisting is fast, and repeated requests
            weigh against you.
          </li>
          <li>
            <Lead>Use the Spamhaus removal centre</Lead> for that specific list. It is free —
            Spamhaus does not charge, and anyone offering paid Spamhaus delisting is not connected
            to Spamhaus.
          </li>
          <li>
            <Lead>Describe the fix concretely.</Lead> "Disabled the compromised account and rotated
            credentials" is a case. "Please remove, we are legitimate" is not.
          </li>
          <li>
            <Lead>Then wait, and monitor.</Lead> Removal is usually hours. Relisting within days
            means the cause is still live.
          </li>
        </LearnList>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'How do I find out which Spamhaus list I am on?',
    a: 'Look at the DNS return code. A query to the zen zone answers with an address in 127.0.0.x, and the last octet names the sub-list: 2 is SBL, 3 is CSS, 4 to 7 is XBL, 10 and 11 are PBL. Every remedy depends on which one, so this is the first step.',
  },
  {
    q: 'What does a PBL listing mean?',
    a: 'That your IP sits in a range the network operator declared should not send mail directly — residential broadband, dynamic ranges, undesignated cloud instances. It is not an accusation of spam and carries no reputation damage. Relay through a proper mail service instead.',
  },
  {
    q: 'Why do I keep getting relisted after removal?',
    a: 'Because the cause was never fixed. Removal is a request, not a repair — if a compromised account is still sending or your list still contains traps, the detection fires again within days. Repeated requests also count against you.',
  },
  {
    q: 'Does Spamhaus charge for removal?',
    a: 'No. Removal is free through the Spamhaus removal centre for every list they operate. Anyone charging for Spamhaus delisting has no connection to Spamhaus and cannot do anything you cannot do yourself.',
  },
  {
    q: 'What is a CSS listing and why did a legitimate sender get one?',
    a: 'CSS is automated and targets patterns rather than reported spam: volume from an IP with no history, rotating across ranges to spread it thinly, or sending to addresses that never engage. Legitimate senders hit it by ramping too fast on a new IP.',
  },
  {
    q: 'My domain is on DBL but my IP is clean. How?',
    a: 'Domains get listed for appearing as a link inside spam, not only for sending it. An affiliate promoting you badly, a compromised page on your site, or an attacker using your domain in their content will all do it. Check the site itself before assuming mail is the cause.',
  },
];
